import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DEFAULT_REQUIRED_FIELDS } from 'addresses/constants';
import { AddressFields } from 'addresses/enums';
import { useAddressActions } from 'addresses/hooks';
import { IAddress, IContactGroupAddressAutocomplete } from 'addresses/types';
import { FormProvider, useNotificationContext } from 'core/states';

import { mapUpsertAddressForm } from './mappers';
import { upsertAddressSchema } from './schema';
import { IUpsertAddressForm } from './types';
import { UpsertAddressForm } from './UpsertAddressForm';
import { enrichSavedAddress, getDefaultValues } from './utils';

const CLOSE_AFTER_SAVE_DELAY_MS = 1000;

const DEFAULT_SHIP_TO_REQUIRED_FIELDS = [
  ...DEFAULT_REQUIRED_FIELDS,
  AddressFields.LINE1,
  AddressFields.CITY,
  AddressFields.STATE,
];

interface IProps {
  isOpen: boolean;
  title: string;
  address?: Partial<IContactGroupAddressAutocomplete>;
  onClose: () => void;
  onSubmitSuccess?: (address: IAddress) => void;
  hasContactFields?: boolean;
  requiredAddressFields?: AddressFields[];
  level?: number;
}

export const UpsertAddress = ({
  isOpen,
  title,
  address,
  onClose,
  onSubmitSuccess,
  hasContactFields = false,
  requiredAddressFields = DEFAULT_SHIP_TO_REQUIRED_FIELDS,
  level,
}: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { saveAddress } = useAddressActions();

  const defaultValues = useMemo(() => getDefaultValues(address), [address]);

  const onSubmit =
    (reset: UseFormReset<IUpsertAddressForm>) => async (data: IUpsertAddressForm) => {
      const savedAddress = await saveAddress(mapUpsertAddressForm(data)).catch(() => null);

      if (!savedAddress?.id) {
        return false;
      }

      onSubmitSuccess?.(enrichSavedAddress(savedAddress, data.address));

      setTimeout(() => handleOnClose(reset), CLOSE_AFTER_SAVE_DELAY_MS);
    };

  // Argument-less reset restores RHF's currently stored default values, which the
  // resetDefaultValues sync keeps up to date. Resetting to the captured `defaultValues`
  // would be stale after a save: onSubmitSuccess updates the parent form's address, so
  // the closure from the submit-time render still holds the pre-edit values.
  const handleOnClose = (reset: UseFormReset<IUpsertAddressForm>) => {
    clearNotification();
    onClose();
    reset();
  };

  return (
    <FormProvider
      schema={upsertAddressSchema(requiredAddressFields, hasContactFields)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertAddressForm
        isOpen={isOpen}
        title={title}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        requiredAddressFields={requiredAddressFields}
        hasContactFields={hasContactFields}
        level={level}
      />
    </FormProvider>
  );
};
